import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  Box,
  Button,
  Grid,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import { fontStyles } from "components/Common/commonStyles";
import FieldLabel from "components/LearnPoint/DTR/FieldLabel";
// ASTT-7319: per-row Preview button, per direct stakeholder-demo
// feedback -- reuses the same shared dialog/service function
// TrainingNotes.js's own Preview button already uses.
import EmailPreviewDialog from "components/LearnPoint/DTR/EmailPreviewDialog";
import { previewDtrEmail } from "services/learnPointUserServices";

// ASTT-7319 follow-up (item 2 of the follow-up thread): a dedicated
// component rather than extending the shared ClassRosterDetailDialog --
// View Projects/View Site are simple, small, read-only tables that don't
// need pagination/filtering/sorting; folding all of that into the shared
// component would have over-complicated those two for a need only this
// one has.
//
// rows here are the RAW backend values (DTRDate/ModifiedDate as whatever
// the stored procedure returns, e.g. an ISO-ish datetime string) --
// deliberately NOT pre-formatted before being passed in, unlike the
// earlier version of this feature. Formatting happens only at render
// time (formatCell below); sorting/filtering operate on real dayjs
// comparisons against the raw values instead, since comparing formatted
// "MM/DD/YYYY" strings as plain text breaks across year boundaries
// (alphabetically "01/01/2027" sorts before "12/31/2026", which is wrong
// chronologically) -- confirmed this would have been a real, if easy to
// miss until tested with a real multi-year DTR history.
const STATUS_OPTIONS = ["Sent", "Draft"];

// ASTT-7319: quick date-range presets, shown before the From/To pickers --
// "Custom" reveals those pickers for manual entry; every other option
// computes the range automatically via computeQuickFilterRange below.
const QUICK_FILTER_OPTIONS = [
  "All",
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "Custom",
];

// dayjs's startOf/endOf("week") follows the locale's own week-start
// convention (Sunday in en-US, this app's locale) -- consistent with how
// a "This Week"/"Last Week" preset is normally understood, not hardcoded
// to a specific day.
const computeQuickFilterRange = (quickFilter) => {
  const today = dayjs();
  switch (quickFilter) {
    case "Today":
      return { from: today.startOf("day"), to: today.endOf("day") };
    case "Yesterday": {
      const yesterday = today.subtract(1, "day");
      return { from: yesterday.startOf("day"), to: yesterday.endOf("day") };
    }
    case "This Week":
      return { from: today.startOf("week"), to: today.endOf("week") };
    case "Last Week": {
      const lastWeek = today.subtract(1, "week");
      return { from: lastWeek.startOf("week"), to: lastWeek.endOf("week") };
    }
    case "This Month":
      return { from: today.startOf("month"), to: today.endOf("month") };
    case "Last Month": {
      const lastMonth = today.subtract(1, "month");
      return { from: lastMonth.startOf("month"), to: lastMonth.endOf("month") };
    }
    default:
      // "All" and "Custom" both resolve to no automatic range -- "All"
      // means no date filtering at all; "Custom" is filled in by the
      // user's own From/To picker selections instead.
      return { from: null, to: null };
  }
};

const COLUMNS = [
  { id: "DTRDate", label: "DTR Date", sortable: true },
  { id: "DTRStatus", label: "Status", sortable: true },
  { id: "ModifiedBy", label: "Last Modified By", sortable: true },
  { id: "ModifiedDate", label: "Last Modified Date", sortable: true },
];

const formatCell = (colId, value) => {
  if (colId === "DTRDate") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : (value ?? "N/A");
  }
  if (colId === "ModifiedDate") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mm A") : (value ?? "N/A");
  }
  return value ?? "N/A";
};

export default function ViewDtrsDialog({
  open,
  onClose,
  rosterId,
  rows = [],
  loading = false,
  onSelectDtr,
}) {
  const [statusFilter, setStatusFilter] = useState(null);
  // ASTT-7319: quickFilter drives dateFrom/dateTo automatically for every
  // preset except "Custom" (handled by the effect below) -- dateFrom/
  // dateTo themselves stay the single source of truth the actual
  // filtering logic reads, so nothing downstream needs to know quickFilter
  // exists at all.
  const [quickFilter, setQuickFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sortColumn, setSortColumn] = useState("DTRDate");
  // Matches the backend's own default ORDER BY DTRDate DESC (most recent
  // first) so the unsorted-yet view still lands on a sensible order.
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // ASTT-7319: per-row Preview -- previewDtrId tracks which row's DTR is
  // currently being previewed, since (unlike TrainingNotes.js/
  // HandOffReport.js, each with exactly one entry in scope) this dialog
  // shows many rows and needs to know which one was clicked.
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDtrId, setPreviewDtrId] = useState(null);

  useEffect(() => {
    if (quickFilter === "Custom") return;
    const { from, to } = computeQuickFilterRange(quickFilter);
    setDateFrom(from);
    setDateTo(to);
    setPage(0);
  }, [quickFilter]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (statusFilter && row.DTRStatus !== statusFilter) return false;
      if (dateFrom || dateTo) {
        const rowDate = dayjs(row.DTRDate);
        if (!rowDate.isValid()) return false;
        if (dateFrom && rowDate.isBefore(dateFrom, "day")) return false;
        if (dateTo && rowDate.isAfter(dateTo, "day")) return false;
      }
      return true;
    });
  }, [rows, statusFilter, dateFrom, dateTo]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      // DTRDate/ModifiedDate compared as real dates; everything else
      // (Status/ModifiedBy) as plain strings.
      if (sortColumn === "DTRDate" || sortColumn === "ModifiedDate") {
        const aDate = dayjs(aVal);
        const bDate = dayjs(bVal);
        if (!aDate.isValid() || !bDate.isValid()) return 0;
        return aDate.valueOf() - bDate.valueOf();
      }
      return String(aVal ?? "").localeCompare(String(bVal ?? ""));
    });
    return sortDirection === "desc" ? sorted.reverse() : sorted;
  }, [filteredRows, sortColumn, sortDirection]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = (colId) => {
    if (sortColumn === colId) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(colId);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    // Reset filters/sort/page on close -- a fresh "View DTRs" click on a
    // different (or the same) roster shouldn't inherit whatever filter
    // was left applied last time.
    setStatusFilter(null);
    setQuickFilter("All");
    setDateFrom(null);
    setDateTo(null);
    setSortColumn("DTRDate");
    setSortDirection("desc");
    setPage(0);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ ...fontStyles, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {`DTRs — Roster ${rosterId}`}
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* ASTT-7319: live count, updates as status/date filters are
            applied -- reads sortedRows (the post-filter, post-sort set),
            since that's the actual number of rows currently visible, not
            the unfiltered total. */}
        <Box sx={{ ...fontStyles, color: "#607d8b", fontSize: "0.85rem", mb: 1.5 }}>
          {sortedRows.length} DTR{sortedRows.length === 1 ? "" : "s"} found
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <FieldLabel>Status</FieldLabel>
            <Autocomplete
              size="small"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(event, value) => {
                setStatusFilter(value);
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="All" />
              )}
            />
          </Grid>
          {/* ASTT-7319: quick filter placed before From/To, per direct
              request -- "All" is the default; every preset besides
              "Custom" computes dateFrom/dateTo automatically (see the
              useEffect above) and hides the manual pickers entirely,
              since there's nothing for the user to set by hand in that
              case. Selecting "Custom" reveals them for manual entry. */}
          <Grid item xs={12} sm={4}>
            <FieldLabel>Quick Filter</FieldLabel>
            <Autocomplete
              size="small"
              disableClearable
              options={QUICK_FILTER_OPTIONS}
              value={quickFilter}
              onChange={(event, value) => {
                if (value) setQuickFilter(value);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          {quickFilter === "Custom" && (
            <>
              <Grid item xs={12} sm={4}>
                <FieldLabel>From Date</FieldLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={dateFrom}
                    onChange={(value) => {
                      setDateFrom(value);
                      setPage(0);
                    }}
                    maxDate={dateTo ?? undefined}
                    renderInput={(params) => (
                      <TextField {...params} size="small" fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FieldLabel>To Date</FieldLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={dateTo}
                    onChange={(value) => {
                      setDateTo(value);
                      setPage(0);
                    }}
                    minDate={dateFrom ?? undefined}
                    renderInput={(params) => (
                      <TextField {...params} size="small" fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id} sx={{ fontWeight: 700 }}>
                        {col.sortable ? (
                          <TableSortLabel
                            active={sortColumn === col.id}
                            direction={sortColumn === col.id ? sortDirection : "asc"}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row, index) => (
                    <TableRow key={row.LP_DTRTrainingNotesId ?? index} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          onClick={() => onSelectDtr?.(row)}
                          sx={{ textTransform: "none" }}
                        >
                          View DTR
                        </Button>
                        {/* ASTT-7319: Preview button per row, per direct
                            stakeholder-demo feedback -- opens the same
                            shared EmailPreviewDialog TrainingNotes.js's
                            own Preview button uses, targeted at this
                            row's own DTR id. */}
                        <Button
                          size="small"
                          onClick={() => {
                            setPreviewDtrId(row.LP_DTRTrainingNotesId);
                            setPreviewDialogOpen(true);
                          }}
                          sx={{ textTransform: "none", ml: 1 }}
                        >
                          Preview
                        </Button>
                      </TableCell>
                      {COLUMNS.map((col) => (
                        <TableCell key={col.id}>
                          {col.id === "DTRStatus" ? (
                            <Chip
                              label={row.DTRStatus ?? "N/A"}
                              size="small"
                              sx={{
                                // ASTT-7319: solid background, white text --
                                // matches the DTR Date calendar's own
                                // Sent/Draft color coding, but using the
                                // BORDER shades from there (#2AA154/
                                // #E0A800) as a solid fill rather than the
                                // light background shades (#DCF5E0/
                                // #FFF4D6), since white text needs real
                                // contrast, not a pastel tint.
                                backgroundColor:
                                  row.DTRStatus === "Sent" ? "#2AA154" : "#E0A800",
                                color: "#fff",
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            formatCell(col.id, row[col.id])
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {paginatedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 3, color: "#9e9e9e" }}>
                        No DTRs found for the selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
      <EmailPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fetchPreview={() => previewDtrEmail(previewDtrId)}
      />
    </>
  );
}

ViewDtrsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rosterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rows: PropTypes.array,
  loading: PropTypes.bool,
  onSelectDtr: PropTypes.func,
};
